import { useMaintenanceRefusal, usePlatformFlags } from '@askporter/api-client';
import type { MaintenanceRefusal } from '@askporter/api-client';
import { useTranslation } from '@askporter/config-provider';

/**
 * Key of the global release flag (henry-rollins) that drives maintenance copy.
 *
 * Declared here rather than imported from api-client, which exports its own for
 * the interceptor path. Two hooks reading one literal is cheaper than making
 * every consumer that mocks `@askporter/api-client` also stub a constant — and
 * a stubbed-away constant fails silently, as `flags[undefined]`, which reads as
 * "no window" and hides the bug rather than surfacing it.
 */
export const MAINTENANCE_MODE_FLAG = 'maintenance-mode';

/**
 * English copy of last resort, used only when the dictionary has no entry for a
 * key and — for the refusal — the response carried no message either.
 */
const EN = {
  title: 'Maintenance in progress',
  message: 'You can keep browsing and reading your data. Saving changes is unavailable until maintenance finishes.',
  refused:
    "Your last change wasn't saved because the platform is read-only during maintenance. Please try again once it finishes.",
};

export interface MaintenanceCopy {
  /** Whether a window is currently open, from the release flag. */
  isMaintenanceMode: boolean;
  /** The most recent refused write, if one has happened. */
  refusal: MaintenanceRefusal | null;
  title: string;
  message: string;
  /** Explanation for a refused write. */
  refusalMessage: string;
  /** Discards the recorded refusal. */
  clearRefusal: () => void;
}

/**
 * The single source of maintenance copy, shared by every surface that has to
 * explain a window — the banner, the refusal toast and the webchat error.
 *
 * **Why `defaultValue` rather than `t(key) || 'literal'`.** This app configures
 * i18next with `keySeparator: false` and no `fallbackLng`, so a missing key
 * resolves to *the key itself*, which is truthy. An `||` fallback therefore
 * never fires and the raw key reaches the screen — which is exactly what
 * shipped, and why the banner read `maintenance_mode:title` in dev.
 * `defaultValue` is applied by i18next on a miss and still reports through
 * `saveMissing`, so the dictionary gap gets recorded rather than hidden.
 *
 * **Why the response message is only a fallback.** The gate sends a human
 * explanation with every refusal, which is always accurate about what it just
 * did — but it is one English string for every tenant. So the translated key
 * wins where it exists, and the gate's own words stand in where it does not.
 */
export const useMaintenanceCopy = (): MaintenanceCopy => {
  const flags = usePlatformFlags();
  const { refusal, clear } = useMaintenanceRefusal();
  const { t } = useTranslation();

  return {
    isMaintenanceMode: Boolean(flags[MAINTENANCE_MODE_FLAG]),
    refusal,
    title: t('ns.maintenance_mode:title', { defaultValue: EN.title }),
    message: t('ns.maintenance_mode:message', { defaultValue: EN.message }),
    refusalMessage: t('ns.maintenance_mode:refused', {
      defaultValue: refusal?.message || EN.refused,
    }),
    clearRefusal: clear,
  };
};
